import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, KeyRound, User, Trash2, AlertTriangle } from "lucide-react";
import { api, useAuth, formatApiError } from "@/lib/auth";
import { toast } from "sonner";

export default function Profile() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();

  const saveUsage = async (v) => {
    try { await api.post("/me/account-usage", { account_usage: v }); await refresh(); toast.success("Préférence enregistrée"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [changingPwd, setChangingPwd] = useState(false);

  const [showDelete, setShowDelete] = useState(false);
  const [deletePwd, setDeletePwd] = useState("");
  const [deletePhrase, setDeletePhrase] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (user) {
      setFirstName(user.first_name || "");
      setLastName(user.last_name || "");
      setEmail(user.email || "");
    }
  }, [user]);

  const saveProfile = async (e) => {
    e?.preventDefault();
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      toast.error("Tous les champs sont requis.");
      return;
    }
    setSavingProfile(true);
    try {
      const { data } = await api.patch("/auth/me", {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim().toLowerCase(),
      });
      if (data?.access_token) {
        localStorage.setItem("cs_token", data.access_token);
      }
      await refresh?.();
      toast.success("Vos informations ont bien été mises à jour.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async (e) => {
    e?.preventDefault();
    if (newPwd.length < 8) {
      toast.error("Le nouveau mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    if (newPwd !== confirmPwd) {
      toast.error("La confirmation du mot de passe ne correspond pas.");
      return;
    }
    setChangingPwd(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPwd,
        new_password: newPwd,
      });
      setCurrentPwd("");
      setNewPwd("");
      setConfirmPwd("");
      toast.success("Mot de passe mis à jour.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setChangingPwd(false);
    }
  };

  const isAdmin = user?.role === "admin";

  const deleteAccount = async (e) => {
    e?.preventDefault();
    if (deletePhrase.trim().toUpperCase() !== "SUPPRIMER") {
      toast.error("Tapez exactement « SUPPRIMER » pour confirmer.");
      return;
    }
    if (!deletePwd) {
      toast.error("Renseignez votre mot de passe actuel pour confirmer.");
      return;
    }
    setDeleting(true);
    try {
      await api.post("/auth/delete-account", { current_password: deletePwd, confirm_phrase: deletePhrase.trim() });
      toast.success("Votre compte a été supprimé. À bientôt.");
      localStorage.removeItem("cs_token");
      // Best-effort context refresh, then hard navigate to home
      try { await logout?.(); } catch {}
      setTimeout(() => { window.location.href = "/"; }, 1800);
    } catch (err) {
      toast.error(formatApiError(err));
      setDeleting(false);
    }
  };

  return (
    <div className="cs-cream min-h-[80vh] py-14" data-testid="page-profile">
      <div className="max-w-[1000px] mx-auto px-6 lg:px-12">
        <button
          onClick={() => navigate(isAdmin ? "/admin" : "/espace")}
          className="text-sm font-mono uppercase tracking-[0.16em] text-neutral-500 hover:text-black inline-flex items-center gap-2"
          data-testid="profile-back"
        >
          <ArrowLeft size={14} /> {isAdmin ? "Retour au back-office" : "Retour à mon espace"}
        </button>

        <div className="mt-4 flex items-start gap-4">
          <div className="w-12 h-12 border border-black/70 flex items-center justify-center shrink-0">
            <User size={20} />
          </div>
          <div>
            <div className="cs-eyebrow text-[#0E7C90]">Compte & sécurité</div>
            <h1 className="cs-h1 mt-2 text-4xl lg:text-5xl">Mon profil</h1>
            <p className="mt-3 text-neutral-600 text-sm max-w-xl">
              Modifiez vos informations personnelles et votre mot de passe. Vos données ne sont
              utilisées que pour l'échange avec l'atelier — Charbonier Studio ne partage jamais
              vos coordonnées.
            </p>
          </div>
        </div>

        <section className="mt-10 grid lg:grid-cols-2 gap-10">
          <form onSubmit={saveProfile} className="cs-card p-6 space-y-4" data-testid="profile-info-form">
            <div className="cs-eyebrow text-neutral-500">Informations personnelles</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="cs-eyebrow text-neutral-500">Prénom</span>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="cs-input mt-2 w-full"
                  data-testid="profile-first-name"
                  autoComplete="given-name"
                />
              </label>
              <label className="block">
                <span className="cs-eyebrow text-neutral-500">Nom</span>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="cs-input mt-2 w-full"
                  data-testid="profile-last-name"
                  autoComplete="family-name"
                />
              </label>
            </div>
            <label className="block">
              <span className="cs-eyebrow text-neutral-500">Adresse email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isAdmin}
                className="cs-input mt-2 w-full disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="profile-email"
                autoComplete="email"
              />
              {isAdmin && (
                <span className="text-[11px] font-mono text-neutral-500 mt-1 block">
                  L'email administrateur ne peut pas être modifié depuis cet écran.
                </span>
              )}
            </label>
            <div className="pt-2">
              <button
                type="submit"
                disabled={savingProfile}
                className="cs-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="profile-save-info"
              >
                <Save size={14} /> Enregistrer
              </button>
            </div>
          </form>

          <form onSubmit={changePassword} className="cs-card p-6 space-y-4" data-testid="profile-password-form">
            <div className="cs-eyebrow text-neutral-500">Changer mon mot de passe</div>
            <label className="block">
              <span className="cs-eyebrow text-neutral-500">Mot de passe actuel</span>
              <input
                type="password"
                value={currentPwd}
                onChange={(e) => setCurrentPwd(e.target.value)}
                className="cs-input mt-2 w-full"
                data-testid="profile-current-password"
                autoComplete="current-password"
              />
            </label>
            <label className="block">
              <span className="cs-eyebrow text-neutral-500">Nouveau mot de passe</span>
              <input
                type="password"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                className="cs-input mt-2 w-full"
                data-testid="profile-new-password"
                autoComplete="new-password"
              />
              <span className="text-[11px] font-mono text-neutral-500 mt-1 block">
                Au moins 8 caractères.
              </span>
            </label>
            <label className="block">
              <span className="cs-eyebrow text-neutral-500">Confirmer le nouveau mot de passe</span>
              <input
                type="password"
                value={confirmPwd}
                onChange={(e) => setConfirmPwd(e.target.value)}
                className="cs-input mt-2 w-full"
                data-testid="profile-confirm-password"
                autoComplete="new-password"
              />
            </label>
            <div className="pt-2">
              <button
                type="submit"
                disabled={changingPwd || !currentPwd || !newPwd || !confirmPwd}
                className="cs-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="profile-save-password"
              >
                <KeyRound size={14} /> Mettre à jour
              </button>
            </div>
          </form>
        </section>

        {user?.role !== "admin" && (
          <section className="mt-10 cs-card p-6" data-testid="profile-account-usage">
            <div className="cs-eyebrow text-[#0E7C90]">Utilisation du compte</div>
            <p className="mt-2 text-sm text-neutral-600">Personnalise uniquement votre accueil. Toutes les fonctionnalités restent disponibles.</p>
            <select value={user?.account_usage || ""} onChange={(e) => saveUsage(e.target.value)} className="cs-input mt-3 max-w-sm" data-testid="profile-account-usage-select">
              <option value="" disabled>Non renseigné</option>
              <option value="own">Mes propres projets</option>
              <option value="clients">Les projets de mes clients</option>
              <option value="both">Les deux</option>
            </select>
          </section>
        )}

        <p className="mt-8 text-[11px] font-mono text-neutral-500">
          Vous ne pouvez pas vous connecter ? Écrivez à Charbonier Studio depuis l'onglet «&nbsp;
          <Link to="/espace/contact" className="cs-link-underline">Contacter le studio</Link>&nbsp;».
        </p>

        {!isAdmin && (
          <section className="mt-14 border border-red-300 bg-red-50/40 p-6" data-testid="profile-danger-zone">
            <div className="flex items-start gap-3">
              <AlertTriangle size={20} className="text-red-700 shrink-0 mt-1" />
              <div className="flex-1">
                <div className="cs-eyebrow text-red-700">Zone sensible · RGPD</div>
                <h2 className="cs-h3 mt-2 text-xl">Supprimer définitivement mon compte</h2>
                <p className="mt-3 text-sm text-neutral-700 leading-relaxed max-w-2xl">
                  Cette action supprime <strong>toutes vos données</strong> chez Charbonier Studio :
                  informations de compte, historique des commandes et des demandes, fichiers déposés,
                  messagerie et conversations générales. <strong>Elle est irréversible.</strong>
                </p>
                <p className="mt-2 text-[12px] font-mono text-neutral-500">
                  Si une commande est en cours et que vous voulez seulement pauser l'atelier,
                  utilisez plutôt «&nbsp;<Link to="/espace/contact" className="cs-link-underline">Contacter le studio</Link>&nbsp;».
                </p>

                {!showDelete ? (
                  <button
                    onClick={() => setShowDelete(true)}
                    data-testid="profile-open-delete"
                    className="mt-5 inline-flex items-center gap-2 px-4 py-3 border border-red-700 text-red-700 hover:bg-red-700 hover:text-white transition-colors text-sm"
                  >
                    <Trash2 size={14} /> Supprimer mon compte
                  </button>
                ) : (
                  <form onSubmit={deleteAccount} className="mt-5 space-y-4 max-w-lg" data-testid="profile-delete-form">
                    <label className="block">
                      <span className="cs-eyebrow text-neutral-500">Mot de passe actuel</span>
                      <input
                        type="password"
                        value={deletePwd}
                        onChange={(e) => setDeletePwd(e.target.value)}
                        className="cs-input mt-2 w-full"
                        data-testid="profile-delete-password"
                        autoComplete="current-password"
                      />
                    </label>
                    <label className="block">
                      <span className="cs-eyebrow text-neutral-500">Tapez « SUPPRIMER » en majuscules pour confirmer</span>
                      <input
                        type="text"
                        value={deletePhrase}
                        onChange={(e) => setDeletePhrase(e.target.value)}
                        placeholder="SUPPRIMER"
                        className="cs-input mt-2 w-full"
                        data-testid="profile-delete-phrase"
                        autoComplete="off"
                      />
                    </label>
                    <div className="flex flex-wrap gap-3 pt-1">
                      <button
                        type="submit"
                        disabled={deleting || !deletePwd || deletePhrase.trim().toUpperCase() !== "SUPPRIMER"}
                        data-testid="profile-confirm-delete"
                        className="inline-flex items-center gap-2 px-4 py-3 bg-red-700 text-white hover:bg-red-800 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Trash2 size={14} /> Confirmer la suppression
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowDelete(false); setDeletePwd(""); setDeletePhrase(""); }}
                        data-testid="profile-cancel-delete"
                        className="inline-flex items-center gap-2 px-4 py-3 border border-black/60 hover:bg-black hover:text-[#FAF8F5] transition-colors text-sm"
                      >
                        Annuler
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
